import { useContext, useState } from 'react';
import { Popover, Tooltip } from '@mui/material';
import { BlockDataPerPage, PagesContext } from '../../../contexts/PagesContext';
import PagePublishModal from '../PageItemPublishModal';
import PageRepublishModal from '../PageItemRepublishModal';
import formatDate from '../../../utils/formatDate';
import URLSettingModal from '../../../components/Blocks/Core/Modals/URLSetting/URLSettingModal';
import { PublishContext } from '../../../contexts/PublishContext';
import { PageStatusEnum } from '../../../enums/PageStatusEnum';
import ModalComponent from '../../../components/ModalComponent';

interface PageRowProps {
  pageData: BlockDataPerPage;
}

const ReleaseStateComponent = ({ pageData, onClick }: { pageData: BlockDataPerPage, onClick: () => void }) => {
  const baseStyle = {
    color: '#fff',
    backgroundColor: '#aaa',
    border: 0,
    fontSize: '10px',
    padding: '4px',
    minWidth: 'auto',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  switch (pageData.status) {
    case PageStatusEnum.PRIVATE:
      return (
        <div
          style={baseStyle}
          className="mode-private-sm mx-auto"
        >
          U
        </div>
      );
    case PageStatusEnum.PROCESSING:
      return (
        <div
          style={{ ...baseStyle, backgroundColor: '#FFA500' }}
          className="mode-processing-sm mx-auto"
        >
          {/* TODO: change this text to japanese */}
          W
        </div>
      );
    case PageStatusEnum.PUBLIC:
      return (
        <div
          style={{ ...baseStyle, backgroundColor: '#2664B1' }}
          className="mode-release-sm mx-auto"
        >
          P
        </div>
      );
    case PageStatusEnum.FAILED:
      return (
        <div
          style={{ ...baseStyle, backgroundColor: '#FF0000', cursor: 'pointer' }}
          className="mode-failed-sm mx-auto"
          onClick={onClick}
        >
          E
        </div>
      );
    default:
      return (
        <div
          style={baseStyle}
          className="mode-private-sm mx-auto"
        >
          U
        </div>
      );
  }
};

const DomainStateComponent = ({
  pageId,
  isOwnDomain,
  parentLocationInList,
}: {
  pageId: string,
  isOwnDomain: boolean,
  parentLocationInList?: number,
}) => {
  const { paginationPageInfo } = useContext(PagesContext);

  const baseStyle = {
    color: '#fff',
    backgroundColor: '#7bb83e',
    border: 0,
    fontSize: '10px',
    padding: '4px',
    minWidth: 'auto',
    width: '20px',
    height: '20px',
    borderRadius: '2px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  if (parentLocationInList) {
    return (
      <div
        style={{ ...baseStyle, backgroundColor: '#008aa6' }}
        className="mode-domain-sm mx-auto"
      >
        {parentLocationInList}
      </div>
    );
  }

  if (isOwnDomain) {
    return (
      <div
        style={{ ...baseStyle, backgroundColor: '#00b3c9' }}
        className="mode-domain-sm mx-auto"
      >
        {Object.keys(paginationPageInfo.total_custom_domains).map((key) => {
          if (pageId === key) {
            return (
              <span key={key}>
                {paginationPageInfo.total_custom_domains[key]}
              </span>
            );
          }

          return (
            <span key={key} />
          );
        })}
      </div>
    );
  }

  return (
    <div
      style={baseStyle}
      className="mode-domain-sm mx-auto"
    >
      S
    </div>
  );
};

const PageRow = ({
  pageData,
}: PageRowProps) => {
  const { publishPage, unpublishPage } = useContext(PublishContext);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [pagePublishId, setPagePublishId] = useState<string | null>(null);
  const [pageRepublishId, setPageRepublishId] = useState<string | null>(null);
  const [configModalOpen, setConfigModalOpen] = useState<boolean>(false);

  // ERROR MODAL
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const handleErrorModalOpen = () => setIsErrorModalOpen(true);
  const handleErrorModalClose = () => setIsErrorModalOpen(false);
  // END ERROR MODAL

  // count every page that is not deleted
  const displayNumber = pageData.subpages ? (pageData.subpages.filter((subpage) => !subpage.deleted).length + 1) : 1;

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const getDomainName = () => {
    if (pageData.settings.is_subpage) {
      return `${pageData.settings.parent_url}/${pageData.settings.subpage_path}`;
    }

    if (pageData.page_setting.custom_url) {
      return pageData.page_setting.custom_url;
    }

    return pageData.page_setting.url;
  };

  const getSiteName = () => {
    if (pageData.settings.is_subpage) {
      return (
        `${pageData.page_setting.title} (${pageData.settings.parent_location}の子ページ)`
      );
    }

    return pageData.page_setting.title;
  };

  return (
    <>
      <tr>
        <td className="text-center">{pageData.settings.sequence_number}</td>
        <td>
          <ReleaseStateComponent
            pageData={pageData}
            onClick={handleErrorModalOpen}
          />
        </td>
        <td>
          <DomainStateComponent
            pageId={pageData.id}
            isOwnDomain={pageData.page_setting.custom_url !== null}
            parentLocationInList={Number(pageData.settings.parent_location)}
          />
        </td>
        <td>{pageData.page_setting.is_published ? formatDate(pageData.page_setting.published_at, 'YYYY/MM/DD H:mm') : ''}</td>
        <td>
          <div className="text-overflow">
            {getSiteName()}
          </div>
        </td>
        <td className="text-center">{displayNumber}</td>
        <td>
          https://
          {getDomainName()}
        </td>
        <td className="table-action text-center">
          <div className="btn-group btn-group-sm">
            <button
              type="button"
              aria-expanded="false"
              data-bs-toggle="dropdown"
              className="btn btn-outline-secondary dropdown-toggle"
              disabled={pageData.status === PageStatusEnum.PROCESSING}
              onClick={handleMenuClick}
            >
              操作を選択
            </button>

            <Popover
              id="menu-id"
              open={Boolean(menuAnchor)}
              anchorEl={menuAnchor}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
            >
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: '0.3rem',
                }}
              >
                {
                  pageData.page_setting.is_published
                    ? (
                      <>
                        <li>
                          <Tooltip
                            arrow
                            title="親ページを処理中状態で操作ができません。"
                            placement="top"
                            disableHoverListener={
                              !pageData.settings.is_subpage
                              || pageData.settings.is_parent_published
                            }
                          >
                            <span>
                              <button
                                disabled={pageData.settings.is_subpage && !pageData.settings.is_parent_published}
                                type="button"
                                className="dropdown-item"
                                data-bs-toggle="modal"
                                data-bs-target="#publishmodeModal2"
                                onClick={
                                  () => {
                                    handleMenuClose();
                                    setPageRepublishId(pageData.id);
                                  }
                                }
                              >
                                <i className="fa-solid fa-arrows-rotate" />
                                {' '}
                                ページを再公開
                              </button>
                            </span>
                          </Tooltip>
                        </li>
                        <li><hr className="dropdown-divider" /></li>
                      </>
                    ) : <></>
                }

                <li>
                  <Tooltip
                    arrow
                    title={pageData.settings?.parent_status === PageStatusEnum.PRIVATE
                      ? '親ページを先に公開してください。'
                      : '親ページを処理中状態で操作ができません。'}
                    placement="top"
                    disableHoverListener={
                      !pageData.settings.is_subpage
                      || pageData.settings.is_parent_published
                    }
                  >
                    <span>
                      <button
                        type="button"
                        className="dropdown-item"
                        data-bs-toggle="modal"
                        data-bs-target="#publishmodeModal2"
                        disabled={
                          pageData.settings.is_subpage
                          && !pageData.settings.is_parent_published
                        }
                        onClick={
                          () => {
                            handleMenuClose();
                            setPagePublishId(pageData.id);
                          }
                        }
                      >
                        <i className="fa-solid fa-globe" />
                        {' '}
                        {pageData.page_setting.is_published ? 'ページを非公開' : 'ページを公開'}
                      </button>
                    </span>
                  </Tooltip>
                </li>

                <li><hr className="dropdown-divider" /></li>
                <li>
                  <a
                    className="dropdown-item py-2"
                    href="#"
                    data-bs-toggle="modal"
                    data-bs-target="#domainsettingmodal"
                    onClick={
                      () => {
                        handleMenuClose();
                        setConfigModalOpen(true);
                      }
                    }
                  >
                    <i className="fa-solid fa-pen-to-square fa-fw" />
                    編集
                  </a>
                </li>
              </ul>
            </Popover>
          </div>
        </td>
      </tr>

      <ModalComponent
        show={isErrorModalOpen}
        closeModal={handleErrorModalClose}
        title="エラー"
        centered
        nofooter
        scrollable
      >
        {pageData.last_error}
      </ModalComponent>

      <PagePublishModal
        open={pagePublishId != null}
        pageTitle={pageData.page_setting.title}
        pageId={pagePublishId}
        isPublished={pageData.page_setting.is_published}
        hasSubpages={pageData.subpages.length > 0}
        handleClose={() => setPagePublishId(null)}
        handleSave={
          pageData.page_setting.is_published
            ? () => unpublishPage(
              pagePublishId,
              setPagePublishId,
            )
            : () => publishPage(
              `/v1/web-process/${pagePublishId}`,
              pagePublishId,
              false,
              setPagePublishId,
            )
        }
      />

      <PageRepublishModal
        open={pageRepublishId != null}
        pageTitle={pageData.page_setting.title}
        pageId={pageRepublishId}
        handleClose={() => setPageRepublishId(null)}
        handleSave={() => publishPage(
          `/v1/web-process/${pageRepublishId}/republish`,
          pageRepublishId,
          true,
          setPageRepublishId,
        )}
      />

      {configModalOpen && (
        <URLSettingModal
          open={configModalOpen}
          setOpen={setConfigModalOpen}
          pageData={pageData}
          isSubpage={pageData.settings.is_subpage}
          setSubpagesOnParent={
            (subpages) => {
              pageData.subpages = subpages;
            }
          }
        />
      )}
    </>
  );
};

export default PageRow;
